import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartErrorEvent } from '@shopify/events';

const DATE_KEY = 'お届け希望日';
const TIME_KEY = 'お届け時間帯';

/**
 * お届け希望日と時間帯を選ばせるコンポーネント。
 *
 * 選択できる範囲（min/max）は Liquid が属性として出力する。カートは
 * morphSection で再描画されるため、ここで属性を付けると再描画のたびに消えて
 * 制限が外れてしまう。このクラスは検証と保存だけを担う。
 *
 * 保存先は data-storage で切り替える。
 *
 * - attributes … 注文全体に紐づく指定。cart attributes に保存する
 * - line       … 別便で発送する商品の指定。その商品行の properties に保存する
 *
 * どちらもフォーム送信ではなく Ajax で保存する。フォーム送信に任せると、加速
 * チェックアウトから会計に進んだ場合に値が送られず注文に残らないため。
 *
 * カートページとドロワーの両方に置かれるため、保存後に相手側へも反映する。
 *
 * @typedef {object} DeliveryDatePickerRefs
 * @property {HTMLInputElement} dateInput - お届け希望日の入力欄。
 * @property {HTMLSelectElement} timeInput - お届け時間帯の選択欄。
 * @property {HTMLElement} errorMessage - エラー表示領域。
 *
 * @extends Component<DeliveryDatePickerRefs>
 */
class DeliveryDatePickerComponent extends Component {
  requiredRefs = ['dateInput', 'timeInput'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();

    this.#validate();
  }

  /**
   * カートの再描画（morphSection）後に呼ばれる。connectedCallback は再実行されない
   * ため、ここで検証をやり直さないとエラー表示とボタンの状態が取り残される。
   */
  updatedCallback() {
    super.updatedCallback();

    this.#validate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#activeFetch?.abort();
  }

  /**
   * 日付または時間帯が変わったときの処理。
   */
  handleChange() {
    if (!this.#validate()) {
      // 休業期間などの不正な日付はカートに保存しない。
      // 保存済みの値が残らないよう、空にして送る。
      this.#save('', this.refs.timeInput.value);
      return;
    }

    this.#save(this.refs.dateInput.value, this.refs.timeInput.value);
  }

  /**
   * 入力値を検証し、問題があればエラーを表示する。
   * @returns {boolean} 保存してよいなら true
   */
  #validate() {
    const { dateInput } = this.refs;
    const value = dateInput.value;

    if (!value) return this.#clearError();

    const selected = this.#parseDate(value);
    if (!selected) return this.#showError('日付の形式が正しくありません。');

    const min = this.#parseDate(dateInput.min);
    const max = this.#parseDate(dateInput.max);

    if (min && selected < min) {
      return this.#showError(`${this.#formatJa(min)}以降の日付をお選びください。`);
    }

    if (max && selected > max) {
      return this.#showError(`${this.#formatJa(max)}までの日付をお選びください。`);
    }

    const closedFrom = this.#parseDate(this.dataset.closedFrom);
    const closedTo = this.#parseDate(this.dataset.closedTo);

    if (closedFrom && closedTo && selected >= closedFrom && selected <= closedTo) {
      return this.#showError(
        `${this.#formatJa(closedFrom)}〜${this.#formatJa(closedTo)}は休業のためご指定いただけません。`
      );
    }

    return this.#clearError();
  }

  /**
   * @param {string} message
   * @returns {false}
   */
  #showError(message) {
    const { errorMessage } = this.refs;

    if (errorMessage) {
      errorMessage.textContent = message;
      errorMessage.hidden = false;
    }

    this.#setCheckoutDisabled('invalid', true);

    return false;
  }

  /** @returns {true} */
  #clearError() {
    const { errorMessage } = this.refs;

    if (errorMessage) {
      errorMessage.textContent = '';
      errorMessage.hidden = true;
    }

    this.#setCheckoutDisabled('invalid', false);

    return true;
  }

  /**
   * レジに進むボタンの有効・無効を切り替える。
   * カートが空のときは元から無効なので、その状態は壊さない。
   *
   * 入力エラーと保存中の2つの理由で無効化するため、理由ごとに印を付けて管理する。
   * どちらか一方が解消しても、もう一方が残っていれば無効のままにする。
   *
   * @param {'invalid'|'saving'} reason
   * @param {boolean} disabled
   */
  #setCheckoutDisabled(reason, disabled) {
    const flag = reason === 'saving' ? 'deliverySaving' : 'deliveryBlocked';

    for (const button of document.querySelectorAll('.cart__checkout-button')) {
      if (!(button instanceof HTMLButtonElement)) continue;

      if (disabled) {
        button.dataset[flag] = 'true';
        button.disabled = true;
        continue;
      }

      if (button.dataset[flag] !== 'true') continue;

      delete button.dataset[flag];

      // もう一方の理由が残っていれば無効のままにする
      if (button.dataset.deliveryBlocked !== 'true' && button.dataset.deliverySaving !== 'true') {
        button.disabled = false;
      }
    }
  }

  /**
   * 選択内容をカートへ保存する。
   *
   * 待ち時間（debounce）は入れない。選んだ直後にレジへ進まれると保存が間に合わず、
   * 指定した日時が注文に残らないため。あわせて保存中はレジに進めないようにする。
   *
   * @param {string} date
   * @param {string} time
   */
  #save = async (date, time) => {
    this.#activeFetch?.abort();

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    this.#setCheckoutDisabled('saving', true);
    this.dataset.saving = 'true';

    try {
      if (this.dataset.storage === 'line') {
        await this.#saveToLine(date, time, abortController.signal);
      } else {
        await this.#saveToAttributes(date, time, abortController.signal);
      }

      this.#syncOtherPickers(date, time);
    } catch (error) {
      // 新しい選択に置き換えられた場合は、後続の保存がボタンを戻すのでここでは何もしない
      if (error instanceof Error && error.name === 'AbortError') return;

      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Failed to update delivery date',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      // 中断された場合は後続の保存が進行中なので、ボタンは戻さない
      if (!abortController.signal.aborted) {
        this.#activeFetch = null;
        this.#setCheckoutDisabled('saving', false);
        delete this.dataset.saving;
      }
    }
  };

  /**
   * 注文全体の指定を cart attributes に保存する。
   *
   * prefix を渡すと「商品名 お届け希望日」のキーで保存する。別便で発送する商品の
   * 指定は商品行に保存されるが、それだけでは注文の「追加詳細」欄に出ないため、
   * 出荷時に見落とさないよう控えを残す用途で使う。
   *
   * @param {string} date
   * @param {string} time
   * @param {AbortSignal} signal
   * @param {string} [prefix]
   */
  async #saveToAttributes(date, time, signal, prefix) {
    const dateKey = prefix ? `${prefix} ${DATE_KEY}` : DATE_KEY;
    const timeKey = prefix ? `${prefix} ${TIME_KEY}` : TIME_KEY;

    const config = fetchConfig('json', {
      body: JSON.stringify({
        attributes: {
          [dateKey]: date,
          [timeKey]: time,
        },
      }),
    });

    const response = await fetch(Theme.routes.cart_update_url, { ...config, signal });

    if (!response.ok) throw new Error(`Failed to update cart attributes: ${response.status}`);

    await response.json();
  }

  /**
   * 別便で発送する商品の指定を、その商品行の properties に保存する。
   *
   * properties は送った内容で丸ごと置き換わるため、ギフトボックスの中身など
   * 既存のプロパティを消さないよう Liquid が出力した値へ差分を重ねて送る。
   *
   * 商品行の表示にも指定内容が出るため、保存後はカートを描画し直す。
   *
   * @param {string} date
   * @param {string} time
   * @param {AbortSignal} signal
   */
  async #saveToLine(date, time, signal) {
    const sectionIds = this.#cartSectionIds();
    const productTitle = this.dataset.productTitle;

    const config = fetchConfig('json', {
      body: JSON.stringify({
        line: Number(this.dataset.line),
        quantity: Number(this.dataset.quantity),
        properties: this.#mergedProperties(date, time),
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });

    const response = await fetch(Theme.routes.cart_change_url, { ...config, signal });

    if (!response.ok) throw new Error(`Failed to update line item: ${response.status}`);

    const result = await response.json();

    if (result.errors) throw new Error(String(result.errors));

    // 商品行のプロパティは注文の「追加詳細」欄には出ないため、控えを残す。
    // 描画し直すと自分の属性が読めなくなるので、その前に済ませる
    if (productTitle) await this.#saveToAttributes(date, time, signal, productTitle);

    this.#renderSections(result.sections);
  }

  /**
   * 既存のプロパティに今回の指定を重ねたものを返す。
   *
   * 空の指定はキーごと消す。空文字のまま送るとチェックアウト画面と注文に
   * 中身のない項目が並んでしまうため。
   *
   * @param {string} date
   * @param {string} time
   * @returns {Record<string, string>}
   */
  #mergedProperties(date, time) {
    /** @type {Record<string, string>} */
    let properties = {};

    try {
      // プロパティを持たない商品では null が入るため、オブジェクトのときだけ引き継ぐ
      const parsed = JSON.parse(this.dataset.properties || '{}');
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) properties = parsed;
    } catch {
      properties = {};
    }

    if (date) {
      properties[DATE_KEY] = date;
    } else {
      delete properties[DATE_KEY];
    }

    if (time) {
      properties[TIME_KEY] = time;
    } else {
      delete properties[TIME_KEY];
    }

    return properties;
  }

  /**
   * 描画し直す対象のセクションIDを集める。カートページとドロワーが同時に
   * 存在する場合があるため、両方を対象にする。
   *
   * @returns {string[]}
   */
  #cartSectionIds() {
    /** @type {Set<string>} */
    const ids = new Set();

    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) {
        ids.add(element.dataset.sectionId);
      }
    }

    return Array.from(ids);
  }

  /**
   * 受け取ったセクションHTMLでカートを描画し直す。
   * ドロワーは開いたままにする必要があるため、描画方法を分ける
   * （テーマ本体の cart-items-component と同じ扱いにしている）。
   *
   * @param {Record<string, string> | undefined} sections
   */
  #renderSections(sections) {
    if (!sections) return;

    for (const [id, html] of Object.entries(sections)) {
      if (typeof html !== 'string') continue;

      const target = document.querySelector(`cart-items-component[data-section-id="${id}"]`);
      const isDrawer = target instanceof HTMLElement && target.dataset.drawer !== undefined;

      morphSection(id, html, { mode: isDrawer ? 'hydration' : 'full' });
    }
  }

  /**
   * カートページとドロワーの両方に同じUIが出るため、もう一方へ選択内容を反映する。
   * 欄が複数並ぶので、同じ保存先を指す欄だけを相手とみなす。
   *
   * @param {string} date
   * @param {string} time
   */
  #syncOtherPickers(date, time) {
    for (const picker of document.querySelectorAll('delivery-date-picker-component')) {
      if (picker === this || !(picker instanceof DeliveryDatePickerComponent)) continue;
      if (picker.dataset.storage !== this.dataset.storage) continue;
      if (this.dataset.storage === 'line' && picker.dataset.line !== this.dataset.line) continue;

      const { dateInput, timeInput } = picker.refs;
      if (dateInput) dateInput.value = date;
      if (timeInput) timeInput.value = time;
    }
  }

  /**
   * YYYY-MM-DD を Date に変換する。不正な値は null を返す。
   * @param {string | undefined} value
   * @returns {Date | null}
   */
  #parseDate(value) {
    if (!value) return null;

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!match) return null;

    const [, year, month, day] = match;
    const date = new Date(Number(year), Number(month) - 1, Number(day));

    // 2026-02-31 のような存在しない日付を弾く
    if (date.getMonth() !== Number(month) - 1 || date.getDate() !== Number(day)) return null;

    return date;
  }

  /**
   * @param {Date} date
   * @returns {string} M月D日
   */
  #formatJa(date) {
    return `${date.getMonth() + 1}月${date.getDate()}日`;
  }
}

if (!customElements.get('delivery-date-picker-component')) {
  customElements.define('delivery-date-picker-component', DeliveryDatePickerComponent);
}

/**
 * 使われなくなった指定を消すだけの要素。
 *
 * 注文全体の欄が消えたあとに残った値や、カートから外された商品の控えを
 * そのままにすると、どの商品にも対応しない日付が注文に入ってしまう。
 * どのキーが不要かは Liquid が判断し、data-key を持つ要素として渡す。
 */
class DeliveryAttributesCleanerComponent extends HTMLElement {
  connectedCallback() {
    /** @type {Record<string, string>} */
    const attributes = {};

    for (const element of this.querySelectorAll('[data-key]')) {
      if (element instanceof HTMLElement && element.dataset.key) attributes[element.dataset.key] = '';
    }

    if (Object.keys(attributes).length === 0) return;

    const config = fetchConfig('json', { body: JSON.stringify({ attributes }) });

    fetch(Theme.routes.cart_update_url, config).catch((error) => {
      console.error('Failed to clear delivery attributes', error);
    });
  }
}

if (!customElements.get('delivery-attributes-cleaner-component')) {
  customElements.define('delivery-attributes-cleaner-component', DeliveryAttributesCleanerComponent);
}
